import { ApiError, BASE, getCsrfToken } from "./constants";

export interface WebGisData {
  subdomain_name: string;
  state: "started" | "disabled" | "suspended" | "error";
  state_transition: "wait_for_create" | "wait_for_unsuspend" | null;
  language: string;
  url: string;
  url_oauth: string;
}

export interface WebGisRefs {
  domain: string;
  scheme: string;
}

export interface ServerLocation {
  id: number;
  display_name: string;
}

export interface CreateWebGisPayload {
  subdomain_name: string;
  language: string;
  location_id: number;
}

export const getMyWebgis = async (signal?: AbortSignal): Promise<WebGisData | null> => {
  const res = await fetch(`${BASE}/webgis/my/`, { signal });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

export const getWebgisRefs = async (): Promise<WebGisRefs> => {
  const res = await fetch(`${BASE}/webgis/refs/`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

export const getServerLocations = async (): Promise<ServerLocation[]> => {
  const res = await fetch(`${BASE}/webgis/cluster/locations/`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

export interface ApiFieldError {
  source: { pointer: string };
  detail: string;
}

export const createWebgis = async (
  payload: CreateWebGisPayload,
): Promise<void> => {
  const res = await fetch(`${BASE}/webgis/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new ApiError(res.status, await res.json());
};

export interface WebGisInfo {
  url: string;
}

export interface UnsuspendWebgisResponse {
  next_ngw?: string;
}

export const checkWebgisUnsuspensionOwner = async (
  signal?: AbortSignal,
): Promise<boolean> => {
  const res = await fetch(`${BASE}/webgis/unsuspension`, { signal });
  const data = await res.json();
  if (
    (res.status === 401 && data.code === "not_owner") ||
    (res.status === 404 && data.code === "instance_not_found")
  ) {
    return false;
  }
  if (!res.ok || typeof data.is_owner !== "boolean") {
    throw new Error(`Unexpected ownership response: HTTP ${res.status}`);
  }
  return data.is_owner;
};

export const unsuspendWebgis = async (
  signal?: AbortSignal,
): Promise<UnsuspendWebgisResponse> => {
  const res = await fetch(`${BASE}/webgis/unsuspension`, {
    method: "POST",
    headers: { "X-CSRFToken": getCsrfToken() },
    signal,
  });
  if (res.status !== 200) throw new Error(`HTTP ${res.status}`);
  return res.json().catch(() => ({}));
};

const waitForWebgisPoll = (intervalMs: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const abort = () => {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", abort);
      resolve();
    }, intervalMs);
    if (signal.aborted) abort();
    else signal.addEventListener("abort", abort, { once: true });
  });

export const pollWebgisUnsuspension = async (
  signal?: AbortSignal,
  maxMs = 60000,
  intervalMs = 1000,
): Promise<WebGisData> => {
  const controller = new AbortController();
  const abort = () => controller.abort();
  const timeout = setTimeout(abort, maxMs);
  signal?.addEventListener("abort", abort, { once: true });
  if (signal?.aborted) abort();
  let lastData: WebGisData | null = null;

  try {
    while (true) {
      const data = await getMyWebgis(controller.signal);
      if (!data) throw new Error("Web GIS instance not found");
      lastData = data;
      if (data.state === "started" || data.state_transition === "wait_for_unsuspend") {
        return data;
      }
      await waitForWebgisPoll(intervalMs, controller.signal);
    }
  } catch (error) {
    if (!signal?.aborted && controller.signal.aborted && lastData) return lastData;
    throw error;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", abort);
  }
};

export const getWebgisInfo = async (
  instanceGuid: string,
): Promise<WebGisInfo> => {
  const res = await fetch(
    `${BASE}/webgis/info/?instance_guid=${encodeURIComponent(instanceGuid)}`,
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

export const contactWebgisOwner = async ({
  instanceGuid,
  message,
}: {
  instanceGuid: string;
  message: string;
}): Promise<void> => {
  const res = await fetch(`${BASE}/webgis/contact_owner/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: JSON.stringify({ instance_guid: instanceGuid, message }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

export const pollWebgisStatus = (
  targetState: string,
  maxMs = 60000,
  intervalMs = 1000,
  signal?: AbortSignal,
): Promise<WebGisData> => {
  return new Promise((resolve, reject) => {
    let lastData: WebGisData | null = null;

    const cleanup = () => {
      clearInterval(checker);
      clearTimeout(timer);
    };

    signal?.addEventListener("abort", () => {
      cleanup();
      reject(new DOMException("Aborted", "AbortError"));
    });

    const checker = setInterval(async () => {
      if (signal?.aborted) return;
      try {
        const data = await getMyWebgis();
        if (data) {
          lastData = data;
          if (data.state === targetState) {
            cleanup();
            resolve(data);
          }
        }
      } catch (err) {
        cleanup();
        reject(err);
      }
    }, intervalMs);

    const timer = setTimeout(() => {
      cleanup();
      if (lastData) resolve(lastData);
      else reject(new Error("Timeout waiting for webgis status"));
    }, maxMs);
  });
};
